import { Box, Button, Typography, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import { RequestFormContext } from "../RequestFormContext";
import { RequestFormView } from "../Enums/RequestFormView";
import { RequestMagicLinkMethod } from "../Enums/RequestMagicLinkMethod";
import { CountDown } from "./CountDown";
import { MagicLinkContactPicker } from "./MagicLinkContactPicker";
import { useEffect } from "react";
import { ResendMagicLinkModal } from "./ResendMagicLinkModal";
import { getYCenterStyles, CardDocumentIcon, ArrowRightIcon, ArrowLeftIcon } from "vig-ui-library";
import { signatureFlowContentGutterSx } from "../Utilities/signatureFlowLayout";

export type MagicLinkSentViewProps = Omit<RequestFormContext, "pathToProp">;

type LinkSentSuccessfullyProps = {
    expiryDateTime: Date;
    onExpired: () => void;
    recipient: string;
    method: RequestMagicLinkMethod;
    isLinkOpened: boolean;
};

const LinkSentSuccessfully = ({ expiryDateTime, onExpired, recipient, method, isLinkOpened }: LinkSentSuccessfullyProps) => {
    const theme = useTheme();

    useEffect(() => {
        const checkExpiry = () => {
            const nowUtc = new Date().getTime();
            const expiry = expiryDateTime.getTime();
            if (nowUtc >= expiry) {
                onExpired();
            }
        };

        checkExpiry();
        const interval = setInterval(checkExpiry, 1000);

        return () => clearInterval(interval);
    }, [expiryDateTime, onExpired]);

    const headline =
        method === RequestMagicLinkMethod.Email
            ? `Link bol odoslaný na emailovú adresu ${recipient}`
            : `Link bol odoslaný na telefónne číslo ${recipient}`;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                backgroundColor: theme.palette.background.default,
                borderRadius: "7px",
            }}
        >
            <Box
                className="section-header"
                sx={{
                    padding: "24px",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    margin: 0,
                }}
            >
                <Box
                    sx={{
                        ...getYCenterStyles(),
                        gap: theme.spacing(2),
                        justifyContent: "flex-start",
                    }}
                >
                    <IoCheckmarkCircle size={32} color={theme.palette.success.main} />
                    <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
                        {headline}
                    </Typography>
                </Box>
            </Box>
            <Box
                className="component-switcher-holder"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                    padding: "24px",
                }}
            >
                {isLinkOpened && (
                    <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                        Link bol otvorený
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                    Môžete počkať v žiadosti alebo odísť. Po potvrdení žiadosti klientom cez link sa žiadosť automaticky odošle na spracovanie.
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                        pt: 0.5,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Link expiruje o
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                        <CountDown expiryDateTime={expiryDateTime} />
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const LinkExpired = () => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                backgroundColor: theme.palette.background.default,
                borderRadius: "7px",
            }}
        >
            <Box
                className="section-header"
                sx={{
                    padding: "24px",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    margin: 0,
                }}
            >
                <Box
                    sx={{
                        ...getYCenterStyles(),
                        gap: theme.spacing(2),
                        justifyContent: "flex-start",
                    }}
                >
                    <IoCloseCircle size={32} color={theme.palette.error.main} />
                    <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
                        Link expiroval
                    </Typography>
                </Box>
            </Box>
            <Box
                className="component-switcher-holder"
                sx={{
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Platnosť linku vypršala. Žiadosť klienta nie je potvrdená. Môžete odoslať nový link alebo zvoliť iný spôsob podpisu.
                </Typography>
            </Box>
        </Box>
    );
};

type LinkNotReceivedCardProps = {
    onResend: () => void;
    recipient: string;
    method: RequestMagicLinkMethod;
};

const LinkNotReceivedCard = ({ onResend, recipient, method }: LinkNotReceivedCardProps) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                backgroundColor: theme.palette.background.default,
                borderRadius: "7px",
            }}
        >
            <Box
                className="section-header"
                sx={{
                    padding: "24px",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    margin: 0,
                }}
            >
                <Box
                    sx={{
                        ...getYCenterStyles(),
                        gap: theme.spacing(2),
                        justifyContent: "flex-start",
                    }}
                >
                    <CardDocumentIcon width={32} height={32} color={theme.palette.primary.main} />
                    <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
                        Link nedorazil?
                    </Typography>
                </Box>
            </Box>
            <Box
                className="component-switcher-holder"
                sx={{
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {method === RequestMagicLinkMethod.Email
                        ? `Ak Magic Link nedorazil, môžete ho odoslať znova na rovnakú emailovú adresu ${recipient} alebo zmeniť spôsob odoslania.`
                        : `Ak Magic Link nedorazil, môžete ho odoslať znova na rovnaké telefónne číslo ${recipient} alebo zmeniť spôsob odoslania.`}
                </Typography>
                <Button variant="outlined" color="error" size="large" onClick={onResend} sx={{ textTransform: "none", alignSelf: "flex-start" }}>
                    Odoslať znovu
                </Button>
            </Box>
        </Box>
    );
};

export const MagicLinkSentView = (props: MagicLinkSentViewProps) => {
    const {
        setView,
        magicLinkExpireAt,
        isMagicLinkOpened,
        magicLinkDeliveryMethod,
        magicLinkRecipient,
        setMagicLinkDeliveryMethod,
        setMagicLinkRecipient,
        magicLinkEmails,
        magicLinkPhones,
        openModal,
        request,
        loadMagicLinkConfigTask,
        getExistingRequestDataOnBackgroundTask,
        rerenderForm,
    } = props;
    const theme = useTheme();
    const expiryDateTime = magicLinkExpireAt;
    const isExpired = expiryDateTime ? new Date().getTime() >= expiryDateTime.getTime() : false;

    const resendDisabled =
        !magicLinkRecipient ||
        magicLinkDeliveryMethod === null ||
        magicLinkDeliveryMethod === undefined;

    const resendArrowColor = resendDisabled ? theme.palette.text.disabled : theme.palette.common.white;
    const resendEndIcon = <ArrowRightIcon width={18} height={18} color={resendArrowColor} />;

    const handleChangeSignatureMethod = async () => {
        try {
            await props.cancelMagicLinkProcessTask.perform();
        } catch {
            // Task handles errors; still navigate back
        }
        setView(RequestFormView.SIGNATURE_METHOD_SELECTION);
    };

    const handleResendLink = () => {
        openModal({
            sx: { width: "100%", maxWidth: "600px", padding: 0 },
            children: (onClose) => (
                <ResendMagicLinkModal
                    onClose={onClose}
                    magicLinkEmails={magicLinkEmails}
                    magicLinkPhones={magicLinkPhones}
                    initialRecipient={magicLinkRecipient}
                    initialDeliveryMethod={magicLinkDeliveryMethod}
                    onCreateLink={async (recipient: string, method: RequestMagicLinkMethod) => {
                        await props.createMagicLinkTask.perform({ recipient, method });

                        const requestId = request.current?.requestId;
                        if (requestId) {
                            await loadMagicLinkConfigTask.perform(requestId);
                        }
                    }}
                    isCreating={props.createMagicLinkTask.isRunning}
                />
            ),
        });
    };

    const handleResendExpiredLink = async () => {
        if (!magicLinkRecipient || magicLinkDeliveryMethod === null) {
            return;
        }

        try {
            await props.createMagicLinkTask.perform();

            const requestId = request.current?.requestId;
            if (requestId) {
                await loadMagicLinkConfigTask.perform(requestId);
            }
        } catch (error) {
            console.error("Failed to resend magic link:", error);
        }
    };

    const handleExpired = async () => {
        const requestId = request.current?.requestId;
        if (requestId) {
            await loadMagicLinkConfigTask.perform(requestId);
        }
    };

    useEffect(() => {
        if (isExpired || !expiryDateTime) {
            return;
        }

        const requestId = request.current?.requestId;
        if (!requestId) {
            return;
        }

        let intervalId: ReturnType<typeof window.setInterval> | undefined;

        const poll = async () => {
            try {
                const config = await loadMagicLinkConfigTask.perform(requestId);
                if (config?.latestMagicLink?.link?.confirmedAt != null) {
                    if (intervalId !== undefined) {
                        window.clearInterval(intervalId);
                        intervalId = undefined;
                    }
                    await getExistingRequestDataOnBackgroundTask.perform();
                    setView(RequestFormView.FORM);
                    rerenderForm();
                }
            } catch {
                // loadMagicLinkConfigTask / getDetail surface errors via existing handlers
            }
        };

        intervalId = window.setInterval(() => {
            void poll();
        }, 10_000);

        return () => {
            if (intervalId !== undefined) {
                window.clearInterval(intervalId);
            }
        };
    }, [isExpired, expiryDateTime, loadMagicLinkConfigTask, getExistingRequestDataOnBackgroundTask, request, setView, rerenderForm]);

    return (
        <Box
            sx={{
                ...signatureFlowContentGutterSx,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                pt: 1,
            }}
        >
            <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                {!isExpired && expiryDateTime ? (
                    <>
                        <LinkSentSuccessfully
                            expiryDateTime={expiryDateTime}
                            onExpired={handleExpired}
                            recipient={magicLinkRecipient || ""}
                            method={magicLinkDeliveryMethod ?? RequestMagicLinkMethod.Sms}
                            isLinkOpened={isMagicLinkOpened}
                        />
                        <LinkNotReceivedCard
                            onResend={handleResendLink}
                            recipient={magicLinkRecipient || ""}
                            method={magicLinkDeliveryMethod ?? RequestMagicLinkMethod.Sms}
                        />
                    </>
                ) : (
                    <>
                        <LinkExpired />
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                backgroundColor: theme.palette.background.default,
                                borderRadius: "7px",
                            }}
                        >
                            <Box
                                className="section-header"
                                sx={{
                                    padding: "24px",
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    margin: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        ...getYCenterStyles(),
                                        gap: theme.spacing(2),
                                        justifyContent: "flex-start",
                                    }}
                                >
                                    <CardDocumentIcon width={32} height={32} color={theme.palette.primary.main} />
                                    <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
                                        Nový Magic Link
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                className="component-switcher-holder"
                                sx={{
                                    padding: "24px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "100%",
                                }}
                            >
                                <MagicLinkContactPicker
                                    magicLinkEmails={magicLinkEmails}
                                    magicLinkPhones={magicLinkPhones}
                                    magicLinkRecipient={magicLinkRecipient}
                                    setMagicLinkRecipient={setMagicLinkRecipient}
                                    magicLinkDeliveryMethod={magicLinkDeliveryMethod}
                                    setMagicLinkDeliveryMethod={setMagicLinkDeliveryMethod}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 2,
                    width: "100%",
                    mt: 5,
                }}
            >
                <LoadingButton
                    variant="text"
                    size="large"
                    loadingPosition="start"
                    loading={props.cancelMagicLinkProcessTask.isRunning}
                    startIcon={
                        <ArrowLeftIcon width={18} height={18} color={theme.palette.text.secondary} />
                    }
                    onClick={handleChangeSignatureMethod}
                    sx={{
                        textTransform: "none",
                        color: theme.palette.text.secondary,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                        },
                    }}
                >
                    Zmeniť spôsob podpisu
                </LoadingButton>
                {isExpired && (
                    <LoadingButton
                        size="large"
                        variant="contained"
                        color="primary"
                        endIcon={resendEndIcon}
                        loadingPosition="end"
                        onClick={handleResendExpiredLink}
                        disabled={resendDisabled}
                        loading={props.createMagicLinkTask.isRunning}
                        sx={{
                            minWidth: { xs: "min(100%, 220px)", sm: 220 },
                            textTransform: "none",
                            marginLeft: "auto",
                        }}
                    >
                        Odoslať link znova
                    </LoadingButton>
                )}
            </Box>
        </Box>
    );
};
